/* eslint-disable max-lines-per-function */

import {
  ApplicationConfig,
  Injector,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MatPaginatorIntl } from '@angular/material/paginator';

import {
  API_BASE_URL_TOKEN,
  AUTH_CONFIG_TOKEN,
  ActionGateAdapter,
  tokenInterceptor,
  AuthService,
  errorInterceptor,
  peticionesEnCursoInterceptor,
  getEnvProfile,
  SpanishPaginatorIntl,
  NebulaTitleStrategy,
  TranslationService,
  BREADCRUMB_SYNC_SERVICE,
  CUSTOM_ICONS_TOKEN,
  DEFAULT_CUSTOM_ICONS,
  UiProfileService,
  TimeZoneService,
  provideNebulaImportExport,
} from '@nebula-shared/data-access';

import {
  BreadcrumbSyncService,
  CENTRO_TAREAS_PATH,
  provideNavegadorCentroTareas,
} from '@nebula-layout/layout';
import { ValidationHelper } from '@nebula-shared/util';
import { provideBuscadorMaestro } from '@nebula-shared/ui';

import {
  NEBULA_ACTION_GATE,
  NEBULA_TRANSLATE,
  NEBULA_UI_CONFIG,
  NebulaNavigationService,
  provideNebulaCoreIcons,
  provideNebulaUIConfig,
} from 'nebula-ui-kit';
import {
  Activity,
  ArchiveRestore,
  ArrowDown01,
  Globe,
  Map as MapIcon,
  MapPin,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Ban,
  BarChart3,
  BetweenHorizontalEnd,
  BookOpen,
  BookText,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CalendarRange,
  CircleCheck,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CircleArrowLeft,
  CircleDollarSign,
  Cloud,
  Ellipsis,
  File,
  FileClock,
  FileDown,
  FilePlus,
  FileSliders,
  FileSpreadsheet,
  FileUp,
  FileUser,
  FileLock,
  FileX,
  Folder,
  FolderArchive,
  FolderGit2,
  FolderKanban,
  FolderLock,
  FolderSync,
  FolderUp,
  Gauge,
  HardDrive,
  History,
  House,
  Inbox,
  Key,
  LayoutTemplate,
  Loader,
  Loader2,
  ListChecks,
  Logs,
  LUCIDE_ICONS,
  LucideIconProvider,
  MessageSquareDot,
  PlusCircle,
  RefreshCw,
  Save,
  SearchCheck,
  Server,
  Shield,
  Sliders,
  SlidersHorizontal,
  SquareKanban,
  SquarePen,
  Table,
  Tags,
  UserKey,
  UserPlus,
  UserCheck,
  UserX,
  Trash2,
  Users,
  UserStar,
  Vault,
  Zap,
  CheckCircle,
  ClockFading,
  Pen,
  ClipboardClock,
  CalendarClock,
  XCircle,
  FileCheck,
  ShieldAlert,
} from 'lucide-angular';
import { proveerCatalogoLucideCompleto } from './iconos/catalogo-lucide.provider';
import localeEsCo from '@angular/common/locales/es-CO';
import { registerLocaleData } from '@angular/common';

registerLocaleData(localeEsCo);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([tokenInterceptor, errorInterceptor, peticionesEnCursoInterceptor]),
    ),
    provideNebulaUIConfig({
      confirmOkText: 'Aceptar',
      confirmCancelText: 'Cancelar',
    }),
    ...provideNebulaCoreIcons(),
    NebulaNavigationService,
    // Dialogos genericos de importar y exportar del kit sobre el origen de Simappe, y el navegador
    // al «Centro de Tareas» del producto activo (HT_ARQ_013, RN-TAR-079).
    provideNebulaImportExport({ rutaCentroTareas: CENTRO_TAREAS_PATH }),
    // La lupa de los campos de codigo abre la ventana de busqueda de maestros (shared-ui).
    provideBuscadorMaestro(),
    // La bandeja de la barra superior y el «Ver tarea» abren el Centro en SU pestaña, como el menu.
    provideNavegadorCentroTareas(),

    // El ayudante de errores de validacion traduce con un servicio estatico que nadie le daba:
    // los formularios que lo usan quedaban invalidos y sin mensaje, y guardar no avisaba nada
    // (Mantis #400: vinculos y cuentas bancarias de Terceros; tambien diferidos, depreciacion,
    // cuentas bancarias y egresos de Tesoreria).
    provideAppInitializer(() => {
      const translation = inject(TranslationService);
      ValidationHelper.setTranslationService(translation);
      return translation.init();
    }),

    // El perfil visual se aplica antes del primer pintado, desde lo que quedo
    // guardado la sesion anterior, para que la pagina no cambie de aspecto a la
    // vista del usuario. Lo vigente se consulta luego, para la proxima sesion.
    provideAppInitializer(() => inject(UiProfileService).start()),

    // La zona horaria del cliente sale del parametro de empresa COMPANY_TIME_ZONE, por el mismo
    // camino que el perfil: se consulta al autenticarse y el kit la lee sin red en cada fecha.
    provideAppInitializer(() => inject(TimeZoneService).start()),

    {
      provide: BREADCRUMB_SYNC_SERVICE,
      useFactory: () => inject(BreadcrumbSyncService),
    },
    {
      provide: CUSTOM_ICONS_TOKEN,
      useValue: DEFAULT_CUSTOM_ICONS,
    },

    {
      // El kit resuelve por aqui las claves de los componentes que reciben CLAVES y no textos.
      // La funcion lee el idioma como signal ANTES de traducir: asi, invocada dentro de un
      // `computed`, ese calculo se rehace solo al cambiar de idioma y la grilla se retraduce.
      provide: NEBULA_TRANSLATE,
      useFactory: () => {
        const translation = inject(TranslationService);
        return (key: string) => {
          translation.lang();
          return translation.translate(key);
        };
      },
    },
    {
      provide: NEBULA_UI_CONFIG,
      useFactory: () => {
        const injector = inject(Injector);
        return {
          navStorageKey: 'nebula_ui_nav_v1',
          searchPlaceholder: 'Buscar',
          // El motor de formularios pinta «Campo requerido» en todo obligatorio sin valor; con
          // la clave del ERP sale en el idioma activo (kit 2.11.0).
          requiredMessageKey: 'LAYOUT.VALIDATION.REQUIRED',
          getSessionCurrency: () => injector.get(AuthService).getSessionCurrency(),
          getSessionTimeZone: () => injector.get(TimeZoneService).zona(),
          // Los errores y advertencias deben alcanzar a leerse: 10 s y 8 s. Exito e informacion
          // siguen con los del kit (3 s y sin cierre), que los mezcla con estos.
          alertDurations: { error: 10000, warning: 8000 },
        };
      },
    },
    {
      provide: LUCIDE_ICONS,
      useFactory: () =>
        new LucideIconProvider({
          Activity,
          ArrowDown01,
          Zap,
          ArchiveRestore,
          Globe,
          Map: MapIcon,
          MapPin,
          ArrowLeft,
          ArrowLeftRight,
          ArrowRight,
          Ban,
          BarChart3,
          BetweenHorizontalEnd,
          BookOpen,
          Briefcase,
          Building,
          Building2,
          Calendar,
          CalendarRange,
          CircleCheck,
          ChevronLeft,
          ChevronRight,
          ChevronDown,
          CircleArrowLeft,
          CircleDollarSign,
          Cloud,
          Ellipsis,
          File,
          FilePlus,
          FileLock,
          FileSliders,
          FileUser,
          Folder,
          FolderArchive,
          FolderGit2,
          FolderKanban,
          FolderLock,
          FolderSync,
          FolderUp,
          Gauge,
          HardDrive,
          History,
          House,
          Inbox,
          Key,
          LayoutTemplate,
          Loader,
          Loader2,
          ListChecks,
          MessageSquareDot,
          PlusCircle,
          RefreshCw,
          Save,
          SearchCheck,
          Server,
          Shield,
          Sliders,
          SlidersHorizontal,
          SquareKanban,
          SquarePen,
          Tags,
          Table,
          UserKey,
          UserPlus,
          UserCheck,
          UserX,
          Trash2,
          Users,
          UserStar,
          Vault,
          Logs,
          FileClock,
          FileSpreadsheet,
          FileDown,
          FileUp,
          BookText,
          FileX,
          CheckCircle,
          ClockFading,
          ShieldAlert,
          FileCheck,
          Pen,
          ClipboardClock,
          CalendarClock,
          XCircle,
        }),
      multi: true,
    },

    // Ultimo recurso de la cadena de iconos: el catalogo COMPLETO de lucide, en archivo aparte.
    // Los registros de arriba se consultan primero y siguen mandando; esto solo cubre lo que
    // ninguno declara, que es lo que dejaba sin icono a las opciones nuevas del menu.
    ...proveerCatalogoLucideCompleto(),

    {
      provide: LOCALE_ID,
      useValue: 'es-CO',
    },

    {
      provide: MatPaginatorIntl,
      useClass: SpanishPaginatorIntl,
    },
    {
      // El titulo de la pestaña del navegador sale del bundle: sin esto Angular
      // aplica el `title` de la ruta tal cual, y una ruta que lo declara como
      // clave termina mostrando el identificador crudo.
      provide: TitleStrategy,
      useClass: NebulaTitleStrategy,
    },

    {
      provide: API_BASE_URL_TOKEN,
      useValue: getEnvProfile().apiBaseUrl,
    },

    {
      provide: AUTH_CONFIG_TOKEN,
      useValue: {
        baseUrl: getEnvProfile().apiBaseUrl,
      },
    },

    {
      provide: NEBULA_ACTION_GATE,
      useExisting: ActionGateAdapter,
    },
  ],
};
